import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { CytoscapeGraphRef } from '@/components/neo4j/cytoscape/CytoscapeGraph'
import { graphApi } from '@/services/api/graph'
import type { DuplicateCandidate } from '@/services/api/manual-graph'
import type { GraphViewNode } from '@/types/graph-view'
import type { CreateEntityDialogProps } from '../CreateEntityDialog'
import { type CanvasNode, manualEntityAsNode } from '../graph-node'

type Position = { x: number; y: number }

interface CanvasEntityCreationOptions {
  caseId: string
  graphRef: RefObject<CytoscapeGraphRef | null>
  /** The nodes on the canvas now. */
  nodes: ReadonlyArray<{ id: string }>
  /** Adds nodes to the view's own data: the graph cache, an investigation's object set. */
  addNodes: (nodes: CanvasNode[]) => void
}

/**
 * "Create entity" on a graph canvas: the toolbar button and the empty-canvas
 * right-click open the schema-driven dialog; the created node joins the view
 * at the right-click point, selected. A duplicate the dialog found is selected
 * instead, loaded into the view first when it is not on the canvas.
 */
export function useCanvasEntityCreation({
  caseId,
  graphRef,
  nodes,
  addNodes,
}: CanvasEntityCreationOptions) {
  const { t } = useTranslation()
  const [createAt, setCreateAt] = useState<{ position?: Position } | null>(null)
  const pending = useRef<{ id: string; position?: Position } | null>(null)

  // Once a node we added is on the canvas: move it, then select and centre it.
  useEffect(() => {
    const target = pending.current
    if (!target || !nodes.some(n => n.id === target.id)) return
    const frame = requestAnimationFrame(() => {
      pending.current = null
      const node = graphRef.current?.getCy()?.$id(target.id)
      if (target.position && node?.nonempty()) node.position(target.position)
      graphRef.current?.selectNodeAndCenter(target.id)
    })
    return () => cancelAnimationFrame(frame)
  }, [graphRef, nodes])

  const reveal = useCallback(
    async (id: string) => {
      if (nodes.some(n => n.id === id)) {
        graphRef.current?.selectNodeAndCenter(id)
        return
      }
      try {
        const node = await graphApi.getNode(caseId, id)
        pending.current = { id }
        addNodes([node])
      } catch {
        toast.error(t('manualGraph.duplicates.notFound'))
      }
    },
    [addNodes, caseId, graphRef, nodes, t]
  )

  const dialogProps: CreateEntityDialogProps = {
    caseId,
    open: createAt !== null,
    onOpenChange: open => {
      if (!open) setCreateAt(null)
    },
    onCreated: (entity: GraphViewNode) => {
      pending.current = { id: entity.id, position: createAt?.position }
      addNodes([manualEntityAsNode(entity)])
    },
    onShowExisting: (candidate: DuplicateCandidate) => void reveal(candidate.id),
  }

  return {
    /** For CytoscapeGraph's `onCreateEntity`: the dialog, remembering where the node goes. */
    openAt: (position?: Position) => setCreateAt({ position }),
    reveal,
    dialogProps,
  }
}
